import { useState, useEffect, useCallback } from 'react';
import { useSecuritySettings } from '@/hooks/useSecuritySettings';

const STORAGE_KEY = 'login_attempts';

interface LoginAttemptData {
  count: number;
  firstAttemptAt: number;
  lockedUntil: number | null;
}

const getAttemptData = (username: string): LoginAttemptData => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${username.toLowerCase()}`);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { count: 0, firstAttemptAt: 0, lockedUntil: null };
};

const setAttemptData = (username: string, data: LoginAttemptData) => {
  localStorage.setItem(`${STORAGE_KEY}_${username.toLowerCase()}`, JSON.stringify(data));
};

const clearAttemptData = (username: string) => {
  localStorage.removeItem(`${STORAGE_KEY}_${username.toLowerCase()}`);
};

export const useLoginLockout = () => {
  const { settings, loading } = useSecuritySettings();

  const isLockedOut = useCallback((username: string): { locked: boolean; remainingSeconds: number } => {
    const data = getAttemptData(username);
    if (data.lockedUntil) {
      const remaining = data.lockedUntil - Date.now();
      if (remaining > 0) {
        return { locked: true, remainingSeconds: Math.ceil(remaining / 1000) };
      }
      // Lockout expired, clear
      clearAttemptData(username);
    }
    return { locked: false, remainingSeconds: 0 };
  }, []);

  const recordFailedAttempt = useCallback((username: string): { locked: boolean; attemptsLeft: number; lockoutMinutes: number } => {
    const data = getAttemptData(username);
    
    // Reset count if first attempt was more than lockout duration ago
    const windowMs = settings.lockoutDurationMinutes * 60 * 1000;
    if (data.firstAttemptAt && Date.now() - data.firstAttemptAt > windowMs) {
      data.count = 0;
      data.firstAttemptAt = Date.now();
    }

    data.count += 1;
    if (data.count === 1) data.firstAttemptAt = Date.now();

    if (data.count >= settings.maxLoginAttempts) {
      data.lockedUntil = Date.now() + settings.lockoutDurationMinutes * 60 * 1000;
      setAttemptData(username, data);
      return { locked: true, attemptsLeft: 0, lockoutMinutes: settings.lockoutDurationMinutes };
    }

    setAttemptData(username, data);
    return { locked: false, attemptsLeft: settings.maxLoginAttempts - data.count, lockoutMinutes: settings.lockoutDurationMinutes };
  }, [settings]);

  const clearAttempts = useCallback((username: string) => {
    clearAttemptData(username);
  }, []);

  return { isLockedOut, recordFailedAttempt, clearAttempts, loading };
};
